import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ActionButton } from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import {
  FieldsGrid,
  useFormSelector
} from '@elfsight/widget-form-builder/widget';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import type { FormLayoutProps } from '../../../common';
import { CheckoutItem, Footer } from '../blocks';
import {
  RECAPTCHA_LEGAL_INFO,
  useCheckoutColors,
  useCheckoutFormSubmit,
  useCheckoutI18n,
  useCheckoutNavigation
} from '../providers';
import {
  defaultGetDisplayedFields,
  useCheckoutFormFieldProps,
  useCheckoutNarrowness
} from '../utils';

const COMPONENT_NAME = 'checkout-form-layout';

export function FormLayout({
  fields,
  getDisplayedFields = defaultGetDisplayedFields,
  headingText,
  captionText,
  footerText,
  withRecaptcha,
  onSubmit
}: FormLayoutProps) {
  const { t } = useCheckoutI18n();
  const { setCheckoutStep } = useCheckoutNavigation();
  const { fieldsError } = useFormSelector();
  const { textColor, headingColor, captionColor, submitButtonColor } =
    useCheckoutColors();
  const { setSubmit } = useCheckoutFormSubmit();
  const isNarrow = useCheckoutNarrowness();
  const fieldProps = useCheckoutFormFieldProps();

  const displayedFields = useMemo(
    () => getDisplayedFields(fields),
    [fields, getDisplayedFields]
  );

  const finalFooterText = useMemo(() => {
    if (!withRecaptcha) {
      return footerText;
    }

    return [footerText, t(RECAPTCHA_LEGAL_INFO)]
      .filter(Boolean)
      .join('<br/><br/>');
  }, [footerText, withRecaptcha, t]);

  const canProceed =
    !displayedFields.length || (!fieldsError.invalid && !fieldsError.required);

  const goToPayment = () => {
    if (canProceed) {
      setCheckoutStep('payment');
    }
  };

  useEffect(() => {
    setSubmit(onSubmit);
  }, [setSubmit, onSubmit]);

  return (
    <Container>
      <Show when={!!headingText || !!captionText}>
        <Header>
          <Typography
            textToken="text24Bold"
            color={headingColor}
            className={createReadableClass({
              component: COMPONENT_NAME,
              element: 'title'
            })}
          >
            {headingText}
          </Typography>

          <Typography
            textToken="text14"
            color={captionColor}
            className={createReadableClass({
              component: COMPONENT_NAME,
              element: 'caption'
            })}
            dangerouslySetInnerHTML={{ __html: captionText }}
          />
        </Header>
      </Show>

      <Show when={isNarrow}>
        <CheckoutItem />

        <Typography
          textToken="text16Bold"
          color={textColor}
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'label'
          })}
        >
          {t('Your Details')}
        </Typography>
      </Show>

      <FieldsContainer>
        <FieldsGrid
          {...fieldProps}
          fields={displayedFields}
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'field'
          })}
        />
      </FieldsContainer>

      <ActionButton
        actionText={t('Go To Payment')}
        color={submitButtonColor}
        onClick={goToPayment}
        disabled={!canProceed}
        maintainAppearanceWhenDisabled
        stretch
      />

      <Show when={!!finalFooterText}>
        <Footer textColor={textColor} text={finalFooterText!} />
      </Show>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({ component: COMPONENT_NAME })
})`
  display: flex;
  flex-direction: column;
  gap: 24px;

  width: 100%;
`;

const Header = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'header'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldsContainer = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'fields-container'
  })
})`
  display: flex;
  flex-direction: column;
  margin-bottom: -24px;
`;
